import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Mail,
  MailOpen,
  Send,
  Clock,
  User,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Inbox,
  Plus,
  Bell,
  BellOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useSupportTicketPulse } from '../hooks/useSupportTicketPulse';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-amber-500', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'bg-blue-500', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-green-500', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-500', icon: CheckCircle },
};

const SupportInbox = ({ darkMode = false }) => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({ from_email: '', subject: '', body: '' });

  // Browser-notification integration — the pulse hook is already running in the
  // parent admin dashboard; here we just expose the enable button + acknowledge
  // that the operator has "seen" the current queue when the inbox is open.
  const { permission, requestPermission, acknowledgeSeen } = useSupportTicketPulse({ enabled: false });

  const fetchTickets = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/support/tickets`, { params });
      setTickets(res.data.tickets || []);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/support/tickets/stats/summary`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchTickets(), fetchStats()]);
      setLoading(false);
      // Operator just opened the inbox — treat everything as "seen" so the
      // next new ticket triggers a notification again.
      acknowledgeSeen();
    };
    load();
  }, [fetchTickets, fetchStats, acknowledgeSeen]);

  // Auto-refresh the ticket list every 30s so new inbound emails appear
  // without requiring a page reload. Matches the cadence of the badge pulse.
  useEffect(() => {
    const id = setInterval(() => {
      fetchTickets();
      fetchStats();
    }, 30_000);
    return () => clearInterval(id);
  }, [fetchTickets, fetchStats]);

  // Also refresh when the window regains focus — instant feedback when the
  // operator alt-tabs back to the admin after seeing the desktop notification.
  useEffect(() => {
    const onFocus = () => {
      fetchTickets();
      fetchStats();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchTickets, fetchStats]);

  const openTicketDetail = async (ticket) => {
    try {
      const res = await axios.get(`${API}/support/tickets/${ticket.id}`);
      setSelectedTicket(res.data);
      setDetailOpen(true);
      setReplyText('');
    } catch (err) {
      toast.error('Failed to load ticket');
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply`, {
        body: replyText,
      });
      toast.success(res.data.email_sent ? 'Reply sent via email' : 'Reply saved (email not sent)');
      setReplyText('');
      // Refresh ticket detail
      const updated = await axios.get(`${API}/support/tickets/${selectedTicket.id}`);
      setSelectedTicket(updated.data);
      fetchTickets();
      fetchStats();
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API}/support/tickets/${ticketId}`, { status: newStatus });
      toast.success(`Ticket marked as ${newStatus}`);
      fetchTickets();
      fetchStats();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    try {
      await axios.post(`${API}/support/tickets`, newTicket);
      toast.success('Ticket created');
      setCreateOpen(false);
      setNewTicket({ from_email: '', subject: '', body: '' });
      fetchTickets();
      fetchStats();
    } catch (err) {
      toast.error('Failed to create ticket');
    }
  };

  const formatDate = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  // Theme classes
  const card = darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-card border';
  const cardText = darkMode ? 'text-white' : 'text-foreground';
  const mutedText = darkMode ? 'text-slate-400' : 'text-muted-foreground';
  const rowBg = darkMode ? 'bg-slate-700/40 hover:bg-slate-700/70' : 'bg-muted/40 hover:bg-muted/70';
  const inputBg = darkMode ? 'bg-slate-800 border-slate-700 text-white' : '';
  const dialogBg = darkMode ? 'bg-slate-800 border-slate-700 text-white' : '';
  const inputDialog = darkMode ? 'bg-slate-700 border-slate-600 text-white' : '';

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total || 0, color: darkMode ? 'text-white' : 'text-foreground' },
          { label: 'Open', value: stats.open || 0, color: 'text-amber-500' },
          { label: 'In Progress', value: stats.in_progress || 0, color: 'text-blue-500' },
          { label: 'Resolved', value: stats.resolved || 0, color: 'text-green-500' },
          { label: 'Closed', value: stats.closed || 0, color: darkMode ? 'text-slate-400' : 'text-muted-foreground' },
        ].map(s => (
          <Card key={s.label} className={card}>
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className={`text-xs ${mutedText}`}>{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className={`w-40 ${inputBg}`} data-testid="ticket-status-filter">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tickets</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={() => { fetchTickets(); fetchStats(); }}
          className={`border-slate-700 ${mutedText}`}
          data-testid="refresh-tickets-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>

        {permission === 'default' && (
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const result = await requestPermission();
              if (result === 'granted') {
                toast.success('Desktop notifications enabled. You\'ll get a ping when new tickets arrive.');
              } else if (result === 'denied') {
                toast.error('Notifications blocked. Update the site setting in your browser to enable.');
              }
            }}
            className={`border-slate-700 ${mutedText}`}
            data-testid="enable-notifications-btn"
            title="Get a desktop notification when a new support email lands"
          >
            <Bell className="w-4 h-4 mr-1" /> Enable Notifications
          </Button>
        )}
        {permission === 'granted' && (
          <span className={`inline-flex items-center gap-1 text-xs ${mutedText}`} data-testid="notifications-enabled-indicator">
            <Bell className="w-3.5 h-3.5 text-green-500" /> Notifications on
          </span>
        )}
        {permission === 'denied' && (
          <span className={`inline-flex items-center gap-1 text-xs ${mutedText}`} title="You blocked notifications in the browser. Adjust site settings to re-enable.">
            <BellOff className="w-3.5 h-3.5 text-red-400" /> Notifications blocked
          </span>
        )}

        <Button
          size="sm"
          onClick={() => setCreateOpen(true)}
          className="bg-purple-600 hover:bg-purple-700 ml-auto"
          data-testid="create-ticket-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New Ticket
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            try {
              const res = await axios.post(`${API}/support/tickets/admin/send-digest?force=true`, null, { withCredentials: true });
              const { sent, recipients, recent_count } = res.data || {};
              if (sent > 0) {
                toast.success(`Digest sent to ${sent} recipient${sent > 1 ? 's' : ''} (${recent_count || 0} recent tickets)`);
              } else {
                toast.warning(res.data?.reason ? `Digest not sent: ${res.data.reason}` : 'Digest not sent');
              }
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Failed to send digest');
            }
          }}
          className={`border-slate-700 ${mutedText}`}
          data-testid="send-support-digest-btn"
          title="Manually send the daily support-ticket digest email now (runs automatically every day at 09:00 UTC)"
        >
          <Send className="w-4 h-4 mr-1" /> Send Digest
        </Button>
      </div>

      {/* Ticket List */}
      <Card className={card}>
        <CardHeader>
          <CardTitle className={`${cardText} flex items-center gap-2`}>
            <Inbox className="w-5 h-5 text-blue-400" />
            Support Inbox
            <Badge variant="outline" className="text-amber-400 border-amber-400 ml-2">
              {stats.open || 0} Open
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No support tickets yet</p>
              <p className="text-sm mt-1">
                Emails to support@americandogclub.com will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {tickets.map(ticket => {
                const cfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                return (
                  <div
                    key={ticket.id}
                    onClick={() => openTicketDetail(ticket)}
                    className={`flex items-center gap-3 p-3 rounded-lg ${rowBg} cursor-pointer transition-colors`}
                    data-testid={`ticket-row-${ticket.id}`}
                  >
                    <div className="flex-shrink-0">
                      {ticket.status === 'open' ? (
                        <Mail className="w-5 h-5 text-amber-400" />
                      ) : (
                        <MailOpen className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`${cardText} font-medium truncate`}>{ticket.subject}</p>
                        <Badge className={`${cfg.color} text-white text-xs px-1.5 py-0`}>
                          {cfg.label}
                        </Badge>
                      </div>
                      <div className={`flex items-center gap-3 text-xs ${mutedText} mt-1`}>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {ticket.from_email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(ticket.created_at)}
                        </span>
                        {ticket.replies?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className={`${dialogBg} max-w-2xl max-h-[85vh] overflow-y-auto`}>
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-400" />
                  {selectedTicket.subject}
                </DialogTitle>
              </DialogHeader>

              {/* Metadata */}
              <div className={`flex flex-wrap gap-3 text-sm ${mutedText} border-b border-border pb-3`}>
                <span>From: <strong className={cardText}>{selectedTicket.from_email}</strong></span>
                <span>{formatDate(selectedTicket.created_at)}</span>
                <Select
                  value={selectedTicket.status}
                  onValueChange={(v) => handleStatusChange(selectedTicket.id, v)}
                >
                  <SelectTrigger className={`w-32 h-7 text-xs ${inputDialog}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Original message */}
              <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-muted/50'} rounded-lg p-4 text-sm`}>
                <p className={`text-xs ${mutedText} mb-2 font-medium`}>Original Message</p>
                {selectedTicket.html_body ? (
                  <div
                    className="prose prose-invert prose-sm max-w-none"
                    // SEC-001 (Feb 2026): sanitize inbound email HTML before
                    // rendering. Support tickets can be created from external
                    // webhooks, so the html_body is untrusted. DOMPurify strips
                    // scripts and event-handler attributes while preserving
                    // basic email formatting (p/b/i/ul/li/a/img).
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedTicket.html_body, {
                        ALLOWED_TAGS: [
                          'p', 'br', 'b', 'i', 'em', 'strong', 'u', 'span',
                          'div', 'a', 'ul', 'ol', 'li', 'blockquote',
                          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                          'img', 'code', 'pre', 'hr', 'table', 'thead',
                          'tbody', 'tr', 'th', 'td',
                        ],
                        ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel'],
                        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
                        ADD_ATTR: ['target'],
                      }),
                    }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans">
                    {selectedTicket.text_body || '(No content)'}
                  </pre>
                )}
              </div>

              {/* Replies */}
              {selectedTicket.replies?.length > 0 && (
                <div className="space-y-3">
                  <p className={`text-xs ${mutedText} font-medium`}>Replies ({selectedTicket.replies.length})</p>
                  {selectedTicket.replies.map(reply => (
                    <div key={reply.id} className="bg-purple-900/20 border border-purple-800/30 rounded-lg p-3 text-sm">
                      <div className={`flex items-center gap-2 text-xs ${mutedText} mb-2`}>
                        <Send className="w-3 h-3" />
                        <span>{reply.sent_by}</span>
                        <span>{formatDate(reply.sent_at)}</span>
                        {reply.email_sent && (
                          <Badge className="bg-green-600 text-white text-xs px-1 py-0">Emailed</Badge>
                        )}
                      </div>
                      <pre className="whitespace-pre-wrap font-sans">{reply.body}</pre>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply box */}
              <div className="border-t border-border pt-3">
                <Textarea
                  placeholder="Type your reply... (will be emailed to the sender)"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={4}
                  className={inputDialog}
                  data-testid="ticket-reply-input"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    onClick={handleReply}
                    disabled={sending || !replyText.trim()}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="send-reply-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                    Send Reply
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className={dialogBg}>
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="From email (optional)"
              value={newTicket.from_email}
              onChange={(e) => setNewTicket({ ...newTicket, from_email: e.target.value })}
              className={inputDialog}
            />
            <Input
              placeholder="Subject *"
              value={newTicket.subject}
              onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
              className={inputDialog}
              data-testid="new-ticket-subject"
            />
            <Textarea
              placeholder="Description"
              value={newTicket.body}
              onChange={(e) => setNewTicket({ ...newTicket, body: e.target.value })}
              rows={4}
              className={inputDialog}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTicket} className="bg-purple-600 hover:bg-purple-700">
              Create Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportInbox;
